import { useMemo } from 'react';
import type { DropdownOption } from '../ui/forms/SearchableDropdown';
import type { ParameterOption } from '../types/sharedParameter.types';
import { parseSharedParameterDataType } from '../lib/revit/sharedParameterDataType';
import { useRevitParameterOptions } from './useRevitParameterOptions';
import { useSharedParameterOptions } from './useSharedParameterOptions';

function normalizeOptionName(value: string): string {
  return value.trim().toLowerCase();
}

export function mergeCompleteParameterOptions(
  sharedOptions: readonly ParameterOption[],
  revitOptions: readonly DropdownOption[]
): ParameterOption[] {
  const optionsByName = new Map(
    sharedOptions.map((option) => [normalizeOptionName(option.value), option])
  );

  revitOptions.forEach((revitOption) => {
    const key = normalizeOptionName(revitOption.value);
    const sharedOption = optionsByName.get(key);
    if (!sharedOption) {
      const dataType = parseSharedParameterDataType(revitOption.dataType);
      optionsByName.set(key, {
        value: revitOption.value,
        label: revitOption.label,
        ...(revitOption.group ? { group: revitOption.group } : {}),
        ...(typeof revitOption.tooltip === 'string'
          ? { description: revitOption.tooltip }
          : {}),
        ...(dataType ? { dataType } : {}),
      });
      return;
    }

    const dataType = parseSharedParameterDataType(revitOption.dataType);
    const merged: ParameterOption = {
      ...sharedOption,
      group: revitOption.group ?? sharedOption.group,
      ...(typeof revitOption.tooltip === 'string'
        ? { description: revitOption.tooltip }
        : {}),
    };
    if (dataType) {
      merged.dataType = dataType;
    } else {
      delete merged.dataType;
    }
    optionsByName.set(key, merged);
  });

  return Array.from(optionsByName.values());
}

export function useCompleteParameterOptions(config?: { enabled?: boolean }) {
  const enabled = config?.enabled ?? true;
  const shared = useSharedParameterOptions();
  const revit = useRevitParameterOptions({ enabled });
  const options = useMemo(
    () => mergeCompleteParameterOptions(shared.options, revit.options),
    [revit.options, shared.options]
  );

  return {
    ...shared,
    options,
    sharedOptions: shared.options,
    isLoading: shared.isLoading || revit.isLoading,
    isLoaded: revit.isLoaded,
    error: shared.error ?? revit.error,
  };
}
